import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  connectChatSocket,
  disconnectChatSocket,
  emitChat,
  getChatSocket,
} from "./socketClient";
import useChatStore from "./useChatStore";
import { getStoredGuestId, setStoredGuestId } from "./chatIds";
import { detectMessageType, uploadChatFile } from "./uploadChatFile";
import {
  ChatMessageType,
  buildMessageTypeFields,
  isPreviewableMedia,
} from "./messageTypes";
import { getImageUrl } from "../utils/media";
import { HostAvailability, getHostAvailability } from "./hostAvailability";

const HISTORY_ACK_TIMEOUT_MS = 8000;
const AVAILABILITY_CHECK_TIMEOUT_MS = 20000;

const extractGuestId = (payload) => {
  if (payload == null) return null;
  if (typeof payload === "string" || typeof payload === "number") {
    return String(payload);
  }
  return (
    payload.guestId ??
    payload.guest_id ??
    payload.id ??
    payload.userId ??
    payload.user?.id ??
    payload.data?.guestId ??
    payload.data?.id ??
    payload.body?.guestId ??
    null
  );
};

/**
 * Parse client:sendPrivateMessage ack.
 * Common shape: { status:200, message:"OK", body:{ messageId, isSeen } }
 */
const extractSendAck = (response) => {
  if (response == null || typeof response !== "object") {
    return { ok: true, serverMsg: null, messageId: null, isSeen: false };
  }

  if (
    response.status === 400 ||
    response.error ||
    response.message === "exception"
  ) {
    return { ok: false, serverMsg: null, messageId: null, isSeen: false };
  }

  const body =
    response.body && typeof response.body === "object"
      ? response.body
      : response.data && typeof response.data === "object"
        ? response.data
        : null;

  const fullMsgCandidate =
    (response.message && typeof response.message === "object"
      ? response.message
      : null) ||
    (body &&
    (body.text != null ||
      body._id != null ||
      body.id != null ||
      body.audioUrl ||
      body.imageUrl ||
      body.videoUrl ||
      body.fileUrl ||
      body.mediaUrl)
      ? body
      : null);

  const messageId =
    body?.messageId ??
    body?.message_id ??
    (fullMsgCandidate?._id != null ? fullMsgCandidate._id : null) ??
    (fullMsgCandidate?.id != null ? fullMsgCandidate.id : null) ??
    response.messageId ??
    null;

  const isSeen =
    body?.isSeen === true ||
    body?.is_seen === true ||
    fullMsgCandidate?.isSeen === true ||
    response.isSeen === true;

  return {
    ok: true,
    serverMsg: fullMsgCandidate,
    messageId: messageId != null ? String(messageId) : null,
    isSeen,
  };
};

/** Parse loadPrivateMessages ack: { status, body: { hostDetails, messages } } */
const extractHistoryResponse = (payload) => {
  if (payload == null) {
    return { messages: [], hostDetails: null, ok: false };
  }

  if (Array.isArray(payload)) {
    return { messages: payload, hostDetails: null, ok: true };
  }

  if (typeof payload !== "object") {
    return { messages: [], hostDetails: null, ok: false };
  }

  const status = payload.status;
  const ok = status == null || status === 200 || status === "OK" || status === "ok";

  const body = payload.body || payload.data || payload;
  const hostDetails = body?.hostDetails || payload.hostDetails || null;
  const messages = Array.isArray(body?.messages)
    ? body.messages
    : Array.isArray(payload.messages)
      ? payload.messages
      : Array.isArray(body)
        ? body
        : Array.isArray(payload.items)
          ? payload.items
          : [];

  return { messages, hostDetails, ok, error: payload.message };
};

const isHostPresencePayload = (payload, hostId) => {
  if (!hostId || payload == null) return false;
  if (typeof payload === "string" || typeof payload === "number") {
    return String(payload) === String(hostId);
  }
  const id =
    payload.userId ??
    payload.id ??
    payload.hostId ??
    payload.host_id ??
    payload.user?.id ??
    payload.sender?.id;
  return id != null && String(id) === String(hostId);
};

/**
 * Owns socket lifecycle for the chat page.
 */
export const useHostChat = ({ hostId, qrCode, unitName, enabled = true }) => {
  const typingTimerRef = useRef(null);
  const isTypingRef = useRef(false);
  const historyRequestedRef = useRef(false);
  const historyAckTimerRef = useRef(null);
  const intentionalDisconnectRef = useRef(false);
  const [connectAttempt, setConnectAttempt] = useState(0);

  const setContext = useChatStore((s) => s.setContext);
  const setConnectionStatus = useChatStore((s) => s.setConnectionStatus);
  const setGuestId = useChatStore((s) => s.setGuestId);
  const setHostOnline = useChatStore((s) => s.setHostOnline);
  const setHostTyping = useChatStore((s) => s.setHostTyping);
  const startHistoryLoad = useChatStore((s) => s.startHistoryLoad);
  const failHistoryLoad = useChatStore((s) => s.failHistoryLoad);
  const setMessagesFromHistory = useChatStore((s) => s.setMessagesFromHistory);
  const setHostDetails = useChatStore((s) => s.setHostDetails);
  const upsertMessage = useChatStore((s) => s.upsertMessage);
  const addOptimisticMessage = useChatStore((s) => s.addOptimisticMessage);
  const markMessageFailed = useChatStore((s) => s.markMessageFailed);
  const markMessageSent = useChatStore((s) => s.markMessageSent);
  const markMessagesSeen = useChatStore((s) => s.markMessagesSeen);
  const applySendAck = useChatStore((s) => s.applySendAck);
  const setSendInFlight = useChatStore((s) => s.setSendInFlight);
  const reset = useChatStore((s) => s.reset);
  const markReclaimed = useChatStore((s) => s.markReclaimed);
  const setReclaiming = useChatStore((s) => s.setReclaiming);
  const setReclaimError = useChatStore((s) => s.setReclaimError);
  const applyEmojiAdded = useChatStore((s) => s.applyEmojiAdded);
  const applyEmojiRemoved = useChatStore((s) => s.applyEmojiRemoved);

  const guestId = useChatStore((s) => s.guestId);
  const reactions = useChatStore((s) => s.reactions);
  const connectionStatus = useChatStore((s) => s.connectionStatus);
  const messages = useChatStore((s) => s.messages);
  const hostOnline = useChatStore((s) => s.hostOnline);
  const hostName = useChatStore((s) => s.hostName);
  const hostAvatar = useChatStore((s) => s.hostAvatar);
  const hostTyping = useChatStore((s) => s.hostTyping);
  const historyLoading = useChatStore((s) => s.historyLoading);
  const historyLoaded = useChatStore((s) => s.historyLoaded);
  const historyError = useChatStore((s) => s.historyError);
  const connectionError = useChatStore((s) => s.connectionError);
  const sendInFlight = useChatStore((s) => s.sendInFlight);
  const lastReclaimAt = useChatStore((s) => s.lastReclaimAt);
  const reclaiming = useChatStore((s) => s.reclaiming);
  const reclaimError = useChatStore((s) => s.reclaimError);

  const clearHistoryAckTimer = useCallback(() => {
    clearTimeout(historyAckTimerRef.current);
    historyAckTimerRef.current = null;
  }, []);

  const failHistory = useCallback(
    (reason) => {
      clearHistoryAckTimer();
      historyRequestedRef.current = false;
      failHistoryLoad(reason);
    },
    [clearHistoryAckTimer, failHistoryLoad],
  );

  const loadHistory = useCallback(
    (resolvedGuestId) => {
      if (!hostId || !qrCode || !resolvedGuestId) return;
      if (historyRequestedRef.current) return;
      historyRequestedRef.current = true;
      startHistoryLoad();

      const socket = getChatSocket();
      if (!socket?.connected) {
        failHistory("not_connected");
        return;
      }

      const payload = {
        qrCode,
        unitName: unitName || "",
        receiverId: String(hostId),
      };

      // Ack shape: { status:200, body:{ hostDetails, messages } }
      socket.emit("client:loadPrivateMessages", payload, (response) => {
        const parsed = extractHistoryResponse(response);
        if (!parsed.ok) {
          failHistory(parsed.error || "load_failed");
          return;
        }
        clearHistoryAckTimer();
        if (parsed.hostDetails) {
          setHostDetails(parsed.hostDetails);
        }
        setMessagesFromHistory(parsed.messages);
      });

      // Fallback: stop spinner if no ack arrives
      clearHistoryAckTimer();
      historyAckTimerRef.current = setTimeout(() => {
        historyAckTimerRef.current = null;
        if (useChatStore.getState().historyLoading) {
          setMessagesFromHistory(useChatStore.getState().messages);
        }
      }, HISTORY_ACK_TIMEOUT_MS);
    },
    [
      hostId,
      qrCode,
      unitName,
      failHistory,
      clearHistoryAckTimer,
      startHistoryLoad,
      setMessagesFromHistory,
      setHostDetails,
    ],
  );

  useEffect(() => {
    if (!enabled || !hostId || !qrCode) return undefined;

    reset();
    historyRequestedRef.current = false;
    intentionalDisconnectRef.current = false;

    const storedGuestId = getStoredGuestId(qrCode);
    setContext({
      hostId,
      qrCode,
      unitName,
      guestId: storedGuestId,
    });
    setConnectionStatus("connecting");

    const socket = connectChatSocket({
      hostId,
      qrCode,
      unitName,
      guestId: storedGuestId,
    });

    const patchSocketGuestId = (id) => {
      if (!socket || !id) return;
      const guestIdStr = String(id);
      socket.auth = {
        ...(typeof socket.auth === "object" && socket.auth ? socket.auth : {}),
        guestId: guestIdStr,
      };
      if (socket.io?.opts) {
        const prevQuery = socket.io.opts.query;
        socket.io.opts.query =
          typeof prevQuery === "object" && prevQuery
            ? { ...prevQuery, guestId: guestIdStr }
            : { guestId: guestIdStr };
      }
    };

    const onConnect = () => {
      setConnectionStatus("connected");
    };

    const onDisconnect = () => {
      historyRequestedRef.current = false;
      if (intentionalDisconnectRef.current) return;
      // No silent reconnect — freeze chat and ask the user to reload.
      setConnectionStatus("error", "connection_lost");
    };

    const onConnectError = (err) => {
      setConnectionStatus(
        "error",
        err?.message || "connection_error",
      );
    };

    const onConnectionError = (payload) => {
      const msg =
        typeof payload === "string"
          ? payload
          : payload?.message || "connection_error";
      setConnectionStatus("error", msg);
    };

    const onUserCreated = (payload) => {
      const id = extractGuestId(payload);
      if (!id) return;
      setGuestId(id);
      setStoredGuestId(qrCode, id);
      patchSocketGuestId(id);
      loadHistory(id);
    };

    const onUserConnected = (payload) => {
      if (isHostPresencePayload(payload, hostId)) {
        setHostOnline(true);
      }
    };

    const onUserDisconnected = (payload) => {
      if (isHostPresencePayload(payload, hostId)) {
        setHostOnline(false);
        setHostTyping(false);
      }
    };

    const onPrivateMessages = (payload) => {
      const parsed = extractHistoryResponse(payload);
      if (parsed.hostDetails) {
        setHostDetails(parsed.hostDetails);
      }
      setMessagesFromHistory(parsed.messages);
    };

    const onNewPrivateMessage = (payload) => {
      // Live message may be the message object, or wrapped like history body
      const msg =
        payload?.body?.messages?.[0] ||
        payload?.message ||
        payload?.data ||
        payload?.body ||
        payload;
      if (Array.isArray(msg)) {
        msg.forEach((item) => upsertMessage(item));
      } else if (msg?.messages && Array.isArray(msg.messages)) {
        msg.messages.forEach((item) => upsertMessage(item));
        if (msg.hostDetails) setHostDetails(msg.hostDetails);
      } else {
        upsertMessage(msg);
      }
      const pending = useChatStore
        .getState()
        .messages.filter((m) => m.status === "pending" && m.tempId);
      if (pending.length === 0) {
        setSendInFlight(false);
      }
    };

    const onUserTyping = (payload) => {
      const currentGuestId = useChatStore.getState().guestId;
      if (isHostPresencePayload(payload, hostId) || payload == null) {
        setHostTyping(true);
      } else {
        const typer =
          payload.userId ?? payload.senderId ?? payload.id ?? payload.receiverId;
        if (typer != null && String(typer) === String(hostId)) {
          setHostTyping(true);
        } else if (
          typer == null ||
          (currentGuestId && String(typer) !== String(currentGuestId))
        ) {
          setHostTyping(true);
        }
      }
    };

    const onUserStoppedTyping = () => {
      setHostTyping(false);
    };

    const onPrivateMessageSeen = (payload) => {
      if (!payload || typeof payload !== "object") return;

      // Ignore receipts for a different unit/chat when unitDetails is present
      const eventQr =
        payload.unitDetails?.qrCode ||
        payload.qrCode ||
        payload.unit_details?.qrCode ||
        null;
      const currentQr = useChatStore.getState().qrCode;
      if (eventQr && currentQr && String(eventQr) !== String(currentQr)) {
        return;
      }

      const messageIds =
        payload.messageIds ||
        payload.ids ||
        (payload.messageId ? [payload.messageId] : null);
      const lastMessageId =
        payload.lastMessageId ??
        payload.last_message_id ??
        payload.lastSeenMessageId ??
        null;

      if (
        !(Array.isArray(messageIds) && messageIds.length > 0) &&
        lastMessageId == null
      ) {
        return;
      }

      markMessagesSeen({
        lastMessageId,
        messageIds: Array.isArray(messageIds) ? messageIds : null,
      });
    };

    const onException = (payload) => {
      const message =
        typeof payload === "string"
          ? payload
          : payload?.message || payload?.error || "exception";

      // Ignore validation noise that is not a failed send
      const lower = String(message).toLowerCase();
      if (
        lower.includes("istyping") ||
        lower.includes("tempid") ||
        lower.includes("already typing")
      ) {
        return;
      }

      // Before history resolves, any exception means the chat can't open
      // (e.g. host offline) — surface it now instead of waiting on history.
      if (!useChatStore.getState().historyLoaded) {
        failHistory(message);
      }
      const pending = useChatStore
        .getState()
        .messages.filter((m) => m.status === "pending" && m.tempId)
        .pop();
      if (pending?.tempId) {
        markMessageFailed(pending.tempId);
      } else {
        setSendInFlight(false);
      }
    };

    const onClientVerify = (payload) => {
      const msg =
        typeof payload === "string"
          ? { messageType: "system", text: payload }
          : payload?.body?.messages?.[0] ||
            (payload?.message && typeof payload.message === "object"
              ? payload.message
              : null) ||
            payload?.data ||
            payload?.body ||
            payload;

      const text = String(
        typeof payload === "string"
          ? payload
          : msg?.text ||
            (typeof payload?.message === "string" ? payload.message : "") ||
            "",
      );

      if (/identity\s+verified/i.test(text)) {
        upsertMessage({
          ...(typeof msg === "object" && msg ? msg : {}),
          id: msg?._id || msg?.id || "__identity_verified__",
          messageType: "system",
          text: text || "Identity Verified!",
          createdAt:
            msg?.timestamp || msg?.createdAt || new Date().toISOString(),
        });
        return;
      }

      if (/verification\s+revoked/i.test(text)) {
        upsertMessage({
          ...(typeof msg === "object" && msg ? msg : {}),
          id: msg?._id || msg?.id || "__verification_revoked__",
          messageType: "system",
          text: text || "Verification Revoked!",
          createdAt:
            msg?.timestamp || msg?.createdAt || new Date().toISOString(),
        });
      }
    };

    const extractEmojiSender = (payload) =>
      payload?.sendedBy?.id ??
      payload?.sendedById ??
      payload?.senderId ??
      payload?.userId ??
      payload?.sender_id ??
      null;

    const onPrivateEmojiAdded = (payload) => {
      const messageId = payload?.messageId || payload?.message_id;
      const emoji = payload?.emoji;
      if (!messageId || !emoji) return;
      applyEmojiAdded({
        messageId: String(messageId),
        emoji: String(emoji).toUpperCase(),
        senderId: extractEmojiSender(payload),
        myId: useChatStore.getState().guestId,
      });
    };

    const onPrivateEmojiRemoved = (payload) => {
      const messageId = payload?.messageId || payload?.message_id;
      const emoji = payload?.emoji;
      if (!messageId || !emoji) return;
      applyEmojiRemoved({
        messageId: String(messageId),
        emoji: String(emoji).toUpperCase(),
        senderId: extractEmojiSender(payload),
        myId: useChatStore.getState().guestId,
      });
    };

    const onAllMessagesRemoved = (payload) => {
      useChatStore.getState().applyAllMessagesRemoved(payload);
    };

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onConnectError);
    socket.on("connectionError", onConnectionError);
    socket.on("userCreated", onUserCreated);
    socket.on("userConnected", onUserConnected);
    socket.on("userDisconnected", onUserDisconnected);
    socket.on("newPrivateMessage", onNewPrivateMessage);
    socket.on("client:verify", onClientVerify);
    socket.on("privateEmojiAdded", onPrivateEmojiAdded);
    socket.on("privateEmojiRemoved", onPrivateEmojiRemoved);
    socket.on("allMessagesRemoved", onAllMessagesRemoved);
    // Some backends emit history under alternate event names
    socket.on("privateMessages", onPrivateMessages);
    socket.on("privateMessagesLoaded", onPrivateMessages);
    socket.on("loadedPrivateMessages", onPrivateMessages);
    socket.on("userTyping", onUserTyping);
    socket.on("userStoppedTyping", onUserStoppedTyping);
    socket.on("privateMessageSeen", onPrivateMessageSeen);
    socket.on("exception", onException);

    if (socket.connected) {
      onConnect();
      if (storedGuestId) {
        loadHistory(storedGuestId);
      }
    }

    return () => {
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }
      clearHistoryAckTimer();
      intentionalDisconnectRef.current = true;
      disconnectChatSocket();
      reset();
    };
  }, [
    enabled,
    hostId,
    qrCode,
    unitName,
    connectAttempt,
    failHistory,
    clearHistoryAckTimer,
    reset,
    setContext,
    setConnectionStatus,
    setGuestId,
    setHostOnline,
    setHostDetails,
    setHostTyping,
    setMessagesFromHistory,
    upsertMessage,
    setSendInFlight,
    markMessageFailed,
    markMessagesSeen,
    applyEmojiAdded,
    applyEmojiRemoved,
    loadHistory,
  ]);

  // Sync history whenever we're connected: initial load once guestId is
  // known, and again after any reconnect (dropped connections can happen
  // mid-flow, e.g. guest switching apps to verify identity), so live events
  // missed while offline aren't lost until a hard page refresh.
  // `loadHistory` guards against duplicate/overlapping requests internally.
  // A failed load stays failed until the guest explicitly retries.
  useEffect(() => {
    if (
      connectionStatus === "connected" &&
      guestId &&
      !historyLoading &&
      !historyError
    ) {
      loadHistory(guestId);
    }
  }, [connectionStatus, guestId, historyLoading, historyError, loadHistory]);

  const availability = useMemo(
    () => getHostAvailability({ connectionStatus, historyLoaded, historyError }),
    [connectionStatus, historyLoaded, historyError],
  );

  // Never leave the guest on an endless loader (e.g. very slow network or
  // the server never creating the guest) — fail so "Try Again" is offered.
  useEffect(() => {
    if (!enabled || availability !== HostAvailability.CHECKING) return undefined;
    const timer = setTimeout(
      () => failHistory("availability_timeout"),
      AVAILABILITY_CHECK_TIMEOUT_MS,
    );
    return () => clearTimeout(timer);
  }, [enabled, availability, connectAttempt, failHistory]);

  const stopTyping = useCallback(() => {
    if (!isTypingRef.current) return;
    isTypingRef.current = false;
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    // Backend has no stop payload fields; server emits userStoppedTyping on its side.
  }, []);

  const notifyTyping = useCallback(() => {
    if (!hostId || !qrCode) return;

    if (!isTypingRef.current) {
      isTypingRef.current = true;
      emitChat("client:privateTyping", {
        qrCode,
        unitName: unitName || "",
        receiverId: String(hostId),
      });
    }

    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }
    typingTimerRef.current = setTimeout(() => {
      stopTyping();
    }, 2000);
  }, [hostId, qrCode, unitName, stopTyping]);

  const sendText = useCallback(
    (text) => {
      const trimmed = (text || "").trim();
      if (!trimmed || !hostId || !qrCode) return false;

      const currentGuestId = useChatStore.getState().guestId;
      if (!currentGuestId) return false;
      if (useChatStore.getState().sendInFlight) return false;

      const tempId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      addOptimisticMessage({ text: trimmed, tempId, messageType: ChatMessageType.TEXT });
      setSendInFlight(true);
      stopTyping();

      const socket = getChatSocket();
      if (!socket?.connected) {
        markMessageFailed(tempId);
        return false;
      }

      const payload = {
        qrCode,
        unitName: unitName || "",
        receiverId: String(hostId),
        ...buildMessageTypeFields(ChatMessageType.TEXT, { text: trimmed }),
      };

      let settled = false;
      const settleSent = (serverMsg, ackMeta = null) => {
        const stillPending = useChatStore
          .getState()
          .messages.find((m) => m.tempId === tempId && m.status === "pending");

        if (stillPending) {
          settled = true;
          markMessageSent(tempId, serverMsg || null, ackMeta);
          return;
        }

        // Timeout may have settled first — still apply real id / isSeen from ack
        if (ackMeta?.messageId || ackMeta?.isSeen) {
          applySendAck(tempId, ackMeta);
        } else if (!settled) {
          setSendInFlight(false);
        }
        settled = true;
      };

      // Prefer Socket.IO ack when backend supports it
      socket.emit("client:sendPrivateMessage", payload, (response) => {
        const ack = extractSendAck(response);
        if (!ack.ok) {
          markMessageFailed(tempId);
          return;
        }
        settleSent(ack.serverMsg, {
          messageId: ack.messageId,
          isSeen: ack.isSeen,
        });
      });

      // Fallback: many gateways don't ack; clear "Sending..." if no exception
      setTimeout(() => settleSent(null), 600);

      return true;
    },
    [
      hostId,
      qrCode,
      unitName,
      addOptimisticMessage,
      setSendInFlight,
      stopTyping,
      markMessageFailed,
      markMessageSent,
      applySendAck,
    ],
  );

  const sendMedia = useCallback(
    async (file, { caption = "", messageType: forcedType } = {}) => {
      if (!file || !hostId || !qrCode) return false;

      const currentGuestId = useChatStore.getState().guestId;
      if (!currentGuestId) return false;
      if (useChatStore.getState().sendInFlight) return false;

      const messageType = forcedType || detectMessageType(file);
      const fileName = file.name || `${messageType}-${Date.now()}`;
      const localPreview = isPreviewableMedia(messageType)
        ? URL.createObjectURL(file)
        : null;
      const tempId = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      const trimmedCaption = (caption || "").trim();
      const text = trimmedCaption || fileName;

      addOptimisticMessage({
        text,
        tempId,
        messageType,
        mediaUrl: localPreview,
        fileName,
        localPreview,
      });
      setSendInFlight(true);
      stopTyping();

      try {
        const uploadedUrl = await uploadChatFile(file);
        const absoluteUrl = getImageUrl(uploadedUrl);

        const socket = getChatSocket();
        if (!socket?.connected) {
          markMessageFailed(tempId);
          return false;
        }

        const payload = {
          qrCode,
          unitName: unitName || "",
          receiverId: String(hostId),
          ...buildMessageTypeFields(messageType, {
            text: trimmedCaption,
            url: absoluteUrl,
          }),
        };

        let settled = false;
        const settleSent = (serverMsg, ackMeta = null) => {
          const stillPending = useChatStore
            .getState()
            .messages.find((m) => m.tempId === tempId && m.status === "pending");

          if (stillPending) {
            settled = true;
            let finalMsg = serverMsg;
            if (!serverMsg) {
              finalMsg = {
                text,
                messageType,
                fileName,
                senderId: currentGuestId,
                isGuest: true,
                ...buildMessageTypeFields(messageType, { url: absoluteUrl }),
              };
            } else if (!serverMsg.senderId && !serverMsg.sender && !serverMsg.isGuest) {
              finalMsg = { ...serverMsg, senderId: currentGuestId, isGuest: true };
            }

            markMessageSent(tempId, finalMsg, ackMeta);
            return;
          }

          if (ackMeta?.messageId || ackMeta?.isSeen) {
            applySendAck(tempId, ackMeta);
          } else if (!settled) {
            setSendInFlight(false);
          }
          settled = true;
        };

        socket.emit("client:sendPrivateMessage", payload, (response) => {
          const ack = extractSendAck(response);
          if (!ack.ok) {
            markMessageFailed(tempId);
            return;
          }
          settleSent(ack.serverMsg, {
            messageId: ack.messageId,
            isSeen: ack.isSeen,
          });
        });

        setTimeout(() => settleSent(null), 800);
        return true;
      } catch (err) {
        markMessageFailed(tempId);
        return false;
      }
    },
    [
      hostId,
      qrCode,
      unitName,
      addOptimisticMessage,
      setSendInFlight,
      stopTyping,
      markMessageFailed,
      markMessageSent,
      applySendAck,
    ],
  );

  const retryMessage = useCallback(
    (tempId) => {
      const msg = useChatStore
        .getState()
        .messages.find((m) => m.tempId === tempId || m.id === `temp:${tempId}`);
      if (!msg) return false;

      useChatStore.getState().removeTempMessage(tempId);

      if (msg.messageType && msg.messageType !== "text" && msg.mediaUrl) {
        // Can't reliably re-upload a blob URL after failure mid-upload;
        // only retry text. For media, user should re-attach.
        if (msg.messageType === "text" || !msg.localPreview) {
          return sendText(msg.text);
        }
        return false;
      }

      if (!msg?.text) return false;
      return sendText(msg.text);
    },
    [sendText],
  );

  const reloadHistory = useCallback(() => {
    useChatStore.setState({ skipHistoryReplace: false });
    historyRequestedRef.current = false;
    const id = useChatStore.getState().guestId;
    if (id) loadHistory(id);
  }, [loadHistory]);

  /**
   * Re-run the availability check: reuse a live socket when possible,
   * otherwise open a fresh connection. Both paths go back to CHECKING.
   */
  const recheckAvailability = useCallback(() => {
    const id = useChatStore.getState().guestId;
    if (getChatSocket()?.connected && id) {
      reloadHistory();
      return;
    }
    setConnectAttempt((n) => n + 1);
  }, [reloadHistory]);

  /**
   * Permanently tear down the socket without triggering the "connection
   * lost" banner — used once the conversation is flagged as blocked or
   * resolved, since those states have their own closed-chat UI.
   */
  const disconnectSocket = useCallback(() => {
    intentionalDisconnectRef.current = true;
    disconnectChatSocket();
  }, []);

  const reclaimSession = useCallback(() => {
    return new Promise((resolve) => {
      if (!hostId || !qrCode || useChatStore.getState().reclaiming) {
        resolve(false);
        return;
      }

      setReclaiming(true);
      setReclaimError(null);

      const socket = getChatSocket();
      if (!socket?.connected) {
        setReclaiming(false);
        setReclaimError("not_connected");
        resolve(false);
        return;
      }

      let settled = false;
      const finish = (ok, error = null) => {
        if (settled) return;
        settled = true;
        setReclaiming(false);
        if (ok) {
          markReclaimed();
        } else {
          setReclaimError(error || "reclaim_failed");
        }
        resolve(ok);
      };

      socket.emit(
        "client:reclaim",
        {
          qrCode,
          unitName: unitName || "",
          receiverId: String(hostId),
        },
        (response) => {
          const failed =
            response?.status === 400 ||
            response?.error ||
            response?.message === "exception";
          finish(!failed, failed ? response?.message : null);
        },
      );

      setTimeout(() => finish(true), 3000);
    });
  }, [hostId, qrCode, unitName, markReclaimed, setReclaiming, setReclaimError]);

  const addReaction = useCallback(
    (messageId, emoji) => {
      if (!messageId || !emoji || !hostId || !qrCode) return false;
      if (String(messageId).startsWith("temp:") || String(messageId).startsWith("__")) {
        return false;
      }

      const socket = getChatSocket();
      if (!socket?.connected) return false;

      const { guestId: currentGuestId } = useChatStore.getState();
      const payload = {
        qrCode,
        unitName: unitName || "",
        messageId: String(messageId),
        emoji: String(emoji).toUpperCase(),
        receiverId: String(hostId),
      };

      socket.emit("client:addPrivateEmoji", payload);
      applyEmojiAdded({
        messageId: String(messageId),
        emoji: payload.emoji,
        senderId: currentGuestId,
        myId: currentGuestId,
      });
      return true;
    },
    [hostId, qrCode, unitName, applyEmojiAdded],
  );

  const removeReaction = useCallback(
    (messageId, emoji) => {
      if (!messageId || !emoji || !hostId || !qrCode) return false;
      if (String(messageId).startsWith("temp:") || String(messageId).startsWith("__")) {
        return false;
      }

      const socket = getChatSocket();
      if (!socket?.connected) return false;

      const { guestId: currentGuestId } = useChatStore.getState();
      const payload = {
        qrCode,
        unitName: unitName || "",
        messageId: String(messageId),
        emoji: String(emoji).toUpperCase(),
        receiverId: String(hostId),
      };

      socket.emit("client:removePrivateEmoji", payload);
      applyEmojiRemoved({
        messageId: String(messageId),
        emoji: payload.emoji,
        senderId: currentGuestId,
        myId: currentGuestId,
      });
      return true;
    },
    [hostId, qrCode, unitName, applyEmojiRemoved],
  );

  return {
    guestId,
    connectionStatus,
    connectionError,
    messages,
    reactions,
    hostOnline,
    hostName,
    hostAvatar,
    hostTyping,
    historyLoading,
    historyLoaded,
    historyError,
    availability,
    sendInFlight,
    sendText,
    sendMedia,
    retryMessage,
    notifyTyping,
    stopTyping,
    reloadHistory,
    recheckAvailability,
    disconnectSocket,
    reclaimSession,
    addReaction,
    removeReaction,
    lastReclaimAt,
    reclaiming,
    reclaimError,
    isConnected: connectionStatus === "connected",
    socket: getChatSocket(),
  };
};

export default useHostChat;
